import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import dayjs, { ManipulateType } from 'dayjs';
import React, { Component } from 'react';
import { ArrowUpward as ArrowUpwardIcon } from '@material-ui/icons';
import { withTranslation } from 'react-i18next';
import { goBack } from 'redux-first-history';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import { convertToLocalTime, generateIdBasedOnPath, safelyDecodeBase64Url } from '@dev/ee-tools';
import {
    BasicTable,
    BasicTableHeadersProps,
    Container,
    DateDropdown,
    EmptyState,
    FilterButton,
    FilterIcon,
    FiltersPopover,
    Header,
    MobileTable,
    NavigateBack,
    Pagination,
    palette,
    PrimaryButton,
    SecondaryButton,
    TertiaryButton,
} from '@dev/ee-components';
import { parse } from 'query-string';
import { includes, isEmpty, isNil, map, omit } from 'lodash-es';
import { CampaignType, CompressionFormat, ExportFileFormats } from '@dev/ee-api/ee-api-models';

import { ListAddContactsStart, ListAddStart, ListsLoadStart } from '@/store/lists/actions';
import { GetCampaignByNameActionParams } from '@/store/campaigns/types';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { HideDialog, ShowDialog } from '@/store/dialog/actions';
import { LinkContactsState } from '@/store/reports/types';
import { ExportLinkContacts, LoadLinkContactsAllStart, LoadLinkContactsStart } from '@/store/reports/actions';
import { ApplicationState } from '@/store';
import { StyledProps } from '@/components/Campaigns/CampaignsInterface';
import { GetCampaignByName } from '@/store/campaigns/actions';
import { LONG_DATE_FORMAT, SHORT_DATE_FORMAT, TIME_RANGES } from '@/services/constants';

import { CampaignsPaths, pathsParams } from '@/services/dictionaries/Paths';
import ExportModal from '../../../../ExportModal';
import { AddContactsToList } from '../../../../Contacts/CommonActions/AddToList';
import { ExportLinkContactsParams, LinkContactsParams } from '../../../../../services/ElasticApi';
import { DateOptionsInterface } from './LinkContactsInterface';
import { ShowDialogParams } from '../../../../../store/dialog/types';
import { CampaignReportLinkContactsProps } from './LinkContactsInterface';

const mapStateToProps = (
    state: ApplicationState,
    ownProps: CampaignReportLinkContactsProps
): CampaignReportLinkContactsProps => ({
    ...ownProps,
    browser: state.browser,
    campaigns: state.campaigns,
    reports: state.reports,
    lists: state.lists,
});

const dispatchProps = {
    LoadLinkContactsStart,
    LoadLinkContactsAllStart,
    ExportLinkContacts,
    GetCampaignByName,
    ListsLoadStart,
    ListAddStart,
    ListAddContactsStart,
    ShowDialog,
    HideDialog,
    GoBack: goBack,
};

type DispatchProps = {
    LoadLinkContactsStart(params: LinkContactsParams);
    LoadLinkContactsAllStart(params: LinkContactsParams);
    ExportLinkContacts(params: ExportLinkContactsParams);
    ListsLoadStart: (params: { limit?: number; offset?: number; modifyQueryString?: boolean }) => void;
    ListAddContactsStart: (listName: string, body: { rule?: string; emails?: string[]; allContacts?: boolean }) => void;
    ListAddStart?: (params: { ListName: string; Rule?: string; Emails?: string[]; AllContacts?: boolean }) => void;
    ShowDialog: (params: ShowDialogParams) => void;
    HideDialog: () => void;
    GoBack(): void;
    GetCampaignByName: (params: GetCampaignByNameActionParams) => void;
};

type Props = DispatchProps & CampaignReportLinkContactsProps;

const mobilePadding = {
    left: 16,
    right: 16,
};

const Content = styled.div<StyledProps>`
    padding: 30px 0px;
    background-color: ${(props): string => (props.isMobile ? 'transparent' : `${palette.basic.paper}`)};
    margin-top: ${(props): string => (props.isMobile ? '16px' : '33px')};
    border-radius: 4px;
    border: ${(props): string => (props.isMobile ? 'none' : `1px solid ${palette.gray[3]}`)};
`;

const HeaderButtons = styled.div`
    button:first-child {
        margin-right: 16px;
    }
`;
const HeaderButtonsMobile = styled.div`
    margin-bottom: 32px;

    button:first-child {
        margin-right: 16px;
    }
    .inputsGroupPanelWrapper {
        float: right;
    }
`;

const LinkElement = styled.div`
    margin-top: ${(props: StyledProps): string => (props.mobile ? '20px' : '24px')};
    display: inline-flex;
    ${(props: StyledProps): string =>
        props.breadcrumbs &&
        `
        margin-top: 0;
        margin-bottom: 12px;
        `}
    & .MuiButtonBase-root {
        cursor: pointer;
        justify-content: flex-start;
        font-weight: 400;
        font-size: 14px;
        color: ${palette.indigo[6]};
        white-space: normal;
        text-align: left;
        &:hover {
            background: ${palette.indigo[1]};
        }
        &:focus {
            background: ${palette.indigo[1]};
            & .MuiButton-label span {
                background: transparent;
            }
        }
        & .MuiButton-endIcon {
            height: 20px;
            align-items: center;
        }
    }
`;

const MobileTableBackground = styled.div`
    & tr {
        background: #2d37481a;
    }
    & table {
        margin: 4px 0;
    }
`;

const DateRangeContainer = styled.div`
    display: flex;
    margin-left: 37px;
    margin-bottom: 30px;
`;
const MobileContentWrapper = styled.div`
    background: ${palette.basic.paper};
    border: 1px solid ${palette.gray[3]};
`;

const SkeletonTemplate = {
    email: <Skeleton />,
    clicked: <Skeleton />,
};

class CampaignLinkContacts extends Component<Props, LinkContactsState> {
    public state: LinkContactsState = {
        exportOptions: {
            link: this.props.match.params.link,
            from: null,
            to: null,
            channelName: this.props.match.params.name,
            fileName: null,
            compressionFormat: CompressionFormat.Zip,
            fileFormat: ExportFileFormats.Csv,
        },
        createListOptions: {
            list: '',
            newList: false,
        },
    };

    public componentDidMount(): void {
        window.scrollTo(0, 0);
        const {
            LoadLinkContactsStart,
            ListsLoadStart,
            match,
            campaigns: {
                currentPreview,
                pickedCampaign: { campaign },
            },
            GetCampaignByName,
        } = this.props;

        const { name, type: campaignType } = match.params;
        const decodedName = safelyDecodeBase64Url(name);
        const isAbSplit = campaignType === CampaignType.AbSplit;
        if (!currentPreview?.templates || (isAbSplit && !campaign)) {
            GetCampaignByName({
                name: decodedName,
                campaignType,
                isLoadTemplatesRequired: true,
                isLoadLinkTrackingRequired: campaignType !== CampaignType.AbSplit,
                isSimpleStatsRequired: campaignType === CampaignType.AbSplit,
            });
        }

        const query = parse(location.search);
        const { page, perPage, to, from } = query;
        const fromDate = campaign?.TriggerOptions?.DateToSendOn;

        this.setState({ campaignDateToSendOn: campaign?.TriggerOptions?.DateToSendOn ?? '' });

        const queryParams: LinkContactsParams = {
            link: safelyDecodeBase64Url(match.params.link as string),
            ...(isNil(perPage) ? {} : { perPage: Number(perPage) }),
            activePage: isNil(page) ? 1 : Number(page),
            to: dayjs(to as string)
                .endOf('day')
                .format(LONG_DATE_FORMAT),
            from: isNil(from)
                ? fromDate
                : dayjs(from as string)
                      .startOf('day')
                      .format(LONG_DATE_FORMAT),
            channelName: safelyDecodeBase64Url(match.params.name as string),
        };

        LoadLinkContactsStart(queryParams);
        ListsLoadStart({ limit: 0, offset: 0 });
    }

    private fileNameLinkContactsRef;

    public setFileNameLinkContactsRef = (element) => {
        this.fileNameLinkContactsRef = element;
    };

    private setList(isNewList, listName): void {
        this.setState({ createListOptions: { newList: isNewList, list: listName } });
    }

    private renderNavigation(): JSX.Element {
        const { match, browser } = this.props;
        const isMobile = browser.lessThan.hd;

        return (
            <LinkElement breadcrumbs isMobile={isMobile}>
                <TertiaryButton
                    text={safelyDecodeBase64Url(match.params.name)}
                    startIcon={<NavigateBack color={palette.indigo[6]} />}
                    onClick={this.navigateToOverview.bind(this)}
                />
            </LinkElement>
        );
    }

    private navigateToOverview(): void {
        const { GoBack } = this.props;
        GoBack();
    }

    private getDateDropdownValue = (shouldReturnTimeRangeString: boolean): number | string => {
        const { location } = this.props;
        const query = parse(location.search);

        if (query.from && query.to) {
            const from = dayjs(query.from as string, SHORT_DATE_FORMAT);
            const to = dayjs(query.to as string, SHORT_DATE_FORMAT);
            const datesDiff = to.diff(from, 'day');

            if (includes(TIME_RANGES, datesDiff) && dayjs().isSame(to, 'day')) {
                return shouldReturnTimeRangeString ? datesDiff.toString() : datesDiff;
            } else {
                return DateDropdownOptions.Calendar;
            }
        }

        return DateDropdownOptions.All;
    };

    private renderHeader(): JSX.Element {
        const { t, match, reports, browser, location } = this.props;
        const isMobile = browser.lessThan.hd;
        const dateRangeData = this.getDateRangeData();
        const value = this.getDateDropdownValue(true);
        const title = safelyDecodeBase64Url(match.params.link);
        const query = parse(location.search);
        const startDate = query?.from?.toString();
        const endDate = query?.to?.toString();

        const routeForId =
            CampaignsPaths.CampaignReport?.replace?.(pathsParams.name, '')
                ?.replace(pathsParams.type, '')
                ?.replace(pathsParams.tab, '') ?? '';

        return (
            <>
                {!isMobile && (
                    <Header
                        header={{
                            title,
                        }}
                        customContent={
                            <HeaderButtons>
                                <SecondaryButton
                                    id={generateIdBasedOnPath('create-list-button', routeForId)}
                                    text={t('createlist')}
                                    onClick={this.handleAddToList.bind(this)}
                                    disabled={!reports.totalLinkContactsCount}
                                />
                                <PrimaryButton
                                    id={generateIdBasedOnPath('exportlinks', routeForId)}
                                    onClick={this.openExportLinkContactsModal.bind(this)}
                                    disabled={!reports.totalLinkContactsCount}
                                    size="medium"
                                    text={t('export')}
                                    variant="text"
                                    startIcon={<ArrowUpwardIcon />}
                                />
                            </HeaderButtons>
                        }
                        customTitleColumnSize={9}
                    />
                )}
                {isMobile && (
                    <>
                        <Header
                            header={{
                                title,
                            }}
                        />
                        <HeaderButtonsMobile>
                            <SecondaryButton
                                id={generateIdBasedOnPath('create-list-button', routeForId)}
                                text={t('createlist')}
                                onClick={this.handleAddToList.bind(this)}
                                disabled={!reports.totalLinkContactsCount}
                            />
                            <PrimaryButton
                                id={generateIdBasedOnPath('exportlinks', routeForId)}
                                onClick={this.openExportLinkContactsModal.bind(this)}
                                disabled={!reports.totalLinkContactsCount}
                                size="medium"
                                text={t('export')}
                                variant="text"
                                startIcon={<ArrowUpwardIcon />}
                            />

                            <FiltersPopover
                                tabs={[
                                    {
                                        defaultValue: value,
                                        label: 'Filters',
                                        options: dateRangeData,
                                        type: 'date',
                                        startDate,
                                        endDate,
                                    },
                                ]}
                                onApply={(obj, momentFrom, momentTo): void => {
                                    this.handleDateDropdownClickMobile(obj, momentFrom, momentTo);
                                }}
                                title={t('filters')}
                                translations={{
                                    apply: t('apply'),
                                    close: t('close'),
                                }}
                            >
                                <FilterButton type="button" startIcon={<FilterIcon color={palette.gray[6]} />} />
                            </FiltersPopover>
                        </HeaderButtonsMobile>
                    </>
                )}
            </>
        );
    }

    private renderTable(): JSX.Element {
        const headers = this.getHeaders();
        const { reports, t, browser } = this.props;
        const { linkContactsPagination } = reports;
        const isMobile = browser.lessThan.hd;

        const SkeletonRows = map([...new Array(linkContactsPagination.perPage)], () => SkeletonTemplate);

        return (
            <>
                {!isMobile && (
                    <BasicTable
                        rows={reports.linkContactsLoading ? SkeletonRows : this.getRows()}
                        headers={headers}
                        isEmpty={!reports?.linkContactsLoading && !reports.linkContactsList?.Contacts.length}
                        emptyStateProps={{ text: t('thereisnodatatodisplay') }}
                    />
                )}
                {isMobile && !!reports.linkContactsList?.Contacts.length && (
                    <MobileTableBackground>
                        <MobileTable
                            rows={reports.linkContactsLoading ? SkeletonRows : this.getRowsMobile()}
                            headers={this.getHeadersMobile()}
                        />
                    </MobileTableBackground>
                )}
                {isMobile && !reports?.linkContactsLoading && !reports.linkContactsList?.Contacts.length && (
                    <MobileContentWrapper>
                        <EmptyState text={t('thereisnodatatodisplay')} />
                    </MobileContentWrapper>
                )}
            </>
        );
    }

    private getHeaders(): BasicTableHeadersProps[] {
        const { t } = this.props;

        return [
            { id: 'email', disablePadding: true, label: t('trackedlink') },
            { id: 'clicked', disablePadding: false, label: t('lastclicked'), columnAlign: 'right' },
        ];
    }

    private getHeadersMobile(): string[] {
        const { t } = this.props;

        return [t('trackedlink'), t('lastclicked')];
    }

    private getRowsMobile(): any[] {
        const {
            reports: { linkContactsList },
            t,
        } = this.props;

        return map(linkContactsList?.Contacts, (contact) => ({
            email: contact?.IsDeleted ? t('contactdeleted') : contact.Email,
            clicked: (
                <div>
                    {convertToLocalTime(contact.LastClicked, 'L')}
                    <br />
                    <span style={{ fontSize: '11px', color: palette.gray[5] }}>
                        {convertToLocalTime(contact.LastClicked, 'LT')}
                    </span>
                </div>
            ),
        }));
    }

    private getRows(): any[] {
        const {
            reports: { linkContactsList },
            reports,
            t,
        } = this.props;

        if (!reports.totalLinkContactsCount || !linkContactsList?.Contacts) {
            return [];
        }

        return map(linkContactsList?.Contacts, (contact) => ({
            id: contact.Email,
            email: contact?.IsDeleted ? t('contactdeleted') : contact.Email,
            clicked: (
                <div>
                    {convertToLocalTime(contact.LastClicked, 'L')}
                    <br />
                    <span style={{ fontSize: '11px', color: palette.gray[5] }}>
                        {convertToLocalTime(contact.LastClicked, 'LT')}
                    </span>
                </div>
            ),
        }));
    }

    private changePage(page: number, perPage: number): void {
        const { LoadLinkContactsStart, match, reports } = this.props;
        const rest = omit(reports.linkContactsPagination, ['perPage', 'activePage']);
        const link = safelyDecodeBase64Url(match.params.link);

        LoadLinkContactsStart({
            link,
            channelName: safelyDecodeBase64Url(match.params.name),
            activePage: page,
            perPage,
            ...rest,
        });
    }

    private changeExportFileFormat(data: { value: ExportFileFormats; label: string }): void {
        const { exportOptions } = this.state;

        exportOptions.fileFormat = data.value;

        this.setState({ exportOptions });
    }

    private changeExportCompression(isEnabled: boolean): void {
        const { exportOptions } = this.state;

        exportOptions.compressionFormat = isEnabled ? CompressionFormat.Zip : CompressionFormat.None;

        this.setState({ exportOptions });
    }

    private exportLinkContacts(): void {
        const {
            ExportLinkContacts,
            match,
            reports: { linkContactsPagination },
        } = this.props;
        const { exportOptions } = this.state;
        const fileName = this.fileNameLinkContactsRef.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;
        const link = safelyDecodeBase64Url(match.params.link);

        const exportParams = {
            link,
            channelName: safelyDecodeBase64Url(match.params.name),
            from: linkContactsPagination.from,
            to: linkContactsPagination.to,
            fileFormat,
            compressionFormat: exportOptions.compressionFormat,
            fileName,
        };

        ExportLinkContacts(exportParams);
    }

    private openExportLinkContactsModal(): void {
        const { ShowDialog, t, browser } = this.props;
        const isMobile = browser.lessThan.hd;

        ShowDialog({
            title: t('exportcontacts'),
            body: (
                <ExportModal
                    onCompressionChange={this.changeExportCompression.bind(this)}
                    onFileFormatChange={this.changeExportFileFormat.bind(this)}
                    inputRef={this.setFileNameLinkContactsRef.bind(this)}
                    isMobile={isMobile}
                />
            ),
            confirmLabel: t('export'),
            cancelLabel: t('cancel'),
            onClickConfirm: this.exportLinkContacts.bind(this),
        });
    }

    private getDateRangeData(): DateOptionsInterface[] {
        const { t } = this.props;
        return [
            {
                label: t('all'),
                value: DateDropdownOptions.All,
            },
            {
                label: t('last7days'),
                value: DateDropdownOptions.Last7days,
            },
            {
                label: t('last14days'),
                value: DateDropdownOptions.Last14days,
            },
            {
                label: t('last30days'),
                value: DateDropdownOptions.Last30days,
            },
            {
                label: t('last3months'),
                value: DateDropdownOptions.Last3months,
            },
            {
                label: t('last6months'),
                value: DateDropdownOptions.Last6months,
            },
            {
                label: t('lastyear'),
                value: DateDropdownOptions.Last12months,
            },
            {
                label: t('specificdaterange'),
                value: DateDropdownOptions.Calendar,
                borderBottom: true,
            },
        ];
    }

    private calendarChange = (from: string, to: string): void => {
        const { LoadLinkContactsStart, match, reports } = this.props;
        const fromFormated = dayjs(from).format(LONG_DATE_FORMAT);
        const toFormated = dayjs(to).format(LONG_DATE_FORMAT);

        LoadLinkContactsStart({
            link: safelyDecodeBase64Url(match.params.link),
            channelName: safelyDecodeBase64Url(match.params.name),
            from: fromFormated,
            to: toFormated,
            perPage: reports.linkContactsPagination.perPage,
        });
    };

    private renderDateRange(): JSX.Element {
        const dateRangeData = this.getDateRangeData();
        const value = this.getDateDropdownValue(true);

        return (
            <DateDropdown
                options={dateRangeData}
                defaultValue={value}
                type={'input'}
                returnRawData
                calendarOnChange={this.calendarChange.bind(this)}
                onItemClick={this.handleDateDropdownClick.bind(this)}
                buttonVariant={'tertiary'}
            />
        );
    }

    private handleDateDropdownClick(e, date: { label: string; value: number | string }): void {
        const { LoadLinkContactsStart, match, reports } = this.props;

        const to =
            date.value === DateDropdownOptions.All
                ? dayjs().endOf('day').format(LONG_DATE_FORMAT)
                : dayjs().format(LONG_DATE_FORMAT);
        const from =
            date.value === DateDropdownOptions.All
                ? this.state.campaignDateToSendOn
                : dayjs()
                      .subtract(date.value as number, 'days' as ManipulateType)
                      .format(LONG_DATE_FORMAT);

        const { perPage } = reports.linkContactsPagination;

        LoadLinkContactsStart({
            link: safelyDecodeBase64Url(match.params.link),
            channelName: safelyDecodeBase64Url(match.params.name),
            from,
            to,
            activePage: 1,
            perPage,
        });
    }

    private handleDateDropdownClickMobile(filtersObj, momentFrom, momentTo): void {
        const { LoadLinkContactsStart, match, reports } = this.props;

        const dateValue = filtersObj[0];
        let from;
        let to = dayjs().utc().toJSON();
        const { perPage } = reports.linkContactsPagination;

        switch (dateValue) {
            case '3':
                from = dayjs().subtract(3, 'month').utc().hour(0).minute(0).format();
                break;
            case DateDropdownOptions.Calendar:
                from = dayjs(momentFrom).startOf('day').utc().format();
                to = dayjs(momentTo).utc().toJSON();
                break;
            case DateDropdownOptions.All:
                from = null;
                to = dayjs().endOf('day').format(LONG_DATE_FORMAT);
                break;
            default:
                from = dayjs().subtract(dateValue, 'd').utc().hour(0).minute(0).format();
                break;
        }

        LoadLinkContactsStart({
            link: safelyDecodeBase64Url(match.params.link),
            channelName: safelyDecodeBase64Url(match.params.name),
            from,
            to,
            activePage: 1,
            perPage,
        });
    }

    private handleAddToList(): void {
        const { t, LoadLinkContactsAllStart, match, lists, ShowDialog } = this.props;

        const query = parse(location.search);
        const { to, from } = query;

        const queryParams: LinkContactsParams = {
            link: safelyDecodeBase64Url(match.params.link as string),
            channelName: safelyDecodeBase64Url(match.params.name as string),
            to: dayjs(to as string)
                .endOf('day')
                .format(LONG_DATE_FORMAT),
            from: isNil(from)
                ? this.state.campaignDateToSendOn
                : dayjs(from as string)
                      .startOf('day')
                      .format(LONG_DATE_FORMAT),
        };

        LoadLinkContactsAllStart(queryParams);

        let contactLists = [];
        contactLists = map(
            lists?.collection,
            (list): Record<string, any> => ({
                value: list.ListName,
                label: list.ListName,
            })
        );

        if (!isEmpty(contactLists)) {
            this.setList(false, contactLists[0].value);
        }
        const params: ShowDialogParams = {
            title: t('addcontactsctolist'),
            confirmLabel: t('save'),
            onClickConfirm: (): void => {
                this.handleAddToListConfirm();
            },
            cancelLabel: t('cancel'),
            body: (
                <AddContactsToList
                    isNewList={this.state.createListOptions?.newList}
                    moveContacts={false}
                    onChange={(isNewList, listName) => {
                        this.setList(isNewList, listName);
                    }}
                />
            ),
        };

        ShowDialog(params);
    }

    private handleAddToListConfirm(): void {
        const {
            reports: { linkContactsAllList },
            ListAddStart,
            ListAddContactsStart,
            HideDialog,
        } = this.props;

        let emails = [];
        emails = map(linkContactsAllList?.Contacts, (contact): string => contact.Email);

        this.state.createListOptions.newList
            ? ListAddStart({ ListName: this.state.createListOptions.list, Emails: emails })
            : ListAddContactsStart(this.state.createListOptions.list, { emails });

        HideDialog();
    }

    private renderFooter(): JSX.Element {
        const {
            reports: { totalLinkContactsCount, linkContactsPagination },
            browser,
        } = this.props;
        const isMobile = browser.lessThan.hd;

        return (
            <>
                {!isMobile && (
                    <Pagination
                        dataLength={totalLinkContactsCount}
                        perPage={linkContactsPagination.perPage}
                        activePage={linkContactsPagination.activePage}
                        handlePaginationChange={this.changePage.bind(this)}
                        siblingCount={1}
                        boundaryCount={1}
                    />
                )}
                {isMobile && (
                    <MobileContentWrapper>
                        <Pagination
                            dataLength={totalLinkContactsCount}
                            perPage={linkContactsPagination.perPage}
                            activePage={linkContactsPagination.activePage}
                            handlePaginationChange={this.changePage.bind(this)}
                            siblingCount={1}
                            boundaryCount={1}
                            disableBorderTop
                        />
                    </MobileContentWrapper>
                )}
            </>
        );
    }

    public render(): JSX.Element {
        const { browser, reports } = this.props;
        const isMobile = browser.lessThan.sm;

        return (
            <Container paddingMobile={mobilePadding}>
                <>{this.renderNavigation()}</>
                {this.renderHeader()}
                <Content isMobile={isMobile}>
                    {!isMobile && <DateRangeContainer>{this.renderDateRange()}</DateRangeContainer>}
                    {this.renderTable()}
                    {!reports.linkContactsList?.Contacts?.length ? null : this.renderFooter()}
                </Content>
            </Container>
        );
    }
}

export default compose(
    withTranslation(),
    connect<CampaignReportLinkContactsProps, DispatchProps>(mapStateToProps, dispatchProps)
)(CampaignLinkContacts);
